"use client";
import { API_ENDPOINTS } from "../../../../constants/config";
import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Edit, Trash, Plus } from "lucide-react";

// Modules that use Read/Write (no "(full)" - render as grouped Read/Write checkboxes)
const MODULES_READ_WRITE = [
  { key: "staff", label: "Staff" },
  { key: "vendor", label: "Vendor" },
  { key: "assets", label: "Assets" },
  { key: "students", label: "Students" },
  { key: "teachers", label: "Teachers" },
  { key: "fees", label: "Fees" },
  { key: "announcements", label: "Announcements" },
];

// Inventory has granular actions instead of a plain Read/Write pair.
// "Inventory" itself is the base view/read toggle; each action below implies
// read and also grants that specific ability. A legacy "inventory:write" or
// full "inventory" grant (from before this split existed) satisfies all of them.
const INVENTORY_ACTIONS = [
  { value: "issue", label: "Issue" },
  { value: "purchase", label: "Purchase" },
  { value: "edit", label: "Edit" },
  { value: "history", label: "History" },
  { value: "analytics", label: "Analytics" },
];

// Legacy single permissions (one checkbox each)
const LEGACY_SINGLE = [
  { value: "dashboard", label: "Dashboard" },
  { value: "teacher-attendance", label: "Teacher Attendance" },
  { value: "registration-faces", label: "Registration Faces" },
  { value: "payroll", label: "Payroll" },
  { value: "class-transition", label: "Class Transition" },
  { value: "classroom-mapping", label: "ClassRoom Mapping" },
  { value: "fee-management", label: "Fee Management" },
  { value: "bus-routes", label: "Bus Routes" },
  { value: "holiday", label: "Holiday" },
  { value: "alert", label: "Alert Management" },
  { value: "faq", label: "FAQ" },
  { value: "customer-ledger", label: "Customer Ledger" },
];

// Settings sub-permissions: backend value → short label for UI
const SETTINGS_SUB = [
  { value: "settings-classes", label: "Classes" },
  { value: "settings-sections", label: "Sections" },
  { value: "settings-subjects", label: "Subjects" },
  { value: "settings-academic-years", label: "Academic Years" },
  { value: "settings-receipt-customization", label: "Receipt" },
  { value: "settings-email-management", label: "Email" },
  { value: "settings-late-fee", label: "Late Fee" },
  { value: "settings-attendance-settings", label: "Attendance" },
  { value: "settings-about", label: "About School" },
];

function RoleForm({ role, onClose, onSuccess, permissionsList, allowedPermissionsFromSchool }) {
  const isEdit = !!role;
  const [name, setName] = useState(role?.name ?? "");
  const [permissions, setPermissions] = useState(role?.permissions ?? []);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    user = null;
  }
  const allowedPermissions = Array.isArray(allowedPermissionsFromSchool)
    ? allowedPermissionsFromSchool
    : (user?.permissions || []);
  const isSchoolAdmin = user?.role === "schooladmin";
  const showAllPermissions = user?.role === "superadmin" && !Array.isArray(allowedPermissionsFromSchool);

  const hasModuleAccess = (moduleKey) =>
    showAllPermissions ||
    allowedPermissions.includes(moduleKey) ||
    allowedPermissions.includes(`${moduleKey}:read`) ||
    allowedPermissions.includes(`${moduleKey}:write`);

  const visibleReadWriteModules = MODULES_READ_WRITE.filter((m) => hasModuleAccess(m.key));
  const showInventorySection = hasModuleAccess("inventory");

  const visibleLegacy = LEGACY_SINGLE.filter(
    (p) => showAllPermissions || allowedPermissions.includes(p.value)
  );

  const visibleSettingsSub = SETTINGS_SUB.filter(
    (p) => showAllPermissions || allowedPermissions.includes(p.value)
  );
  const showSettingsSection = showAllPermissions || allowedPermissions.includes("settings") || visibleSettingsSub.length > 0;

  const hasRead = (moduleKey) =>
    permissions.includes(`${moduleKey}:read`) || permissions.includes(moduleKey);
  const hasWrite = (moduleKey) =>
    permissions.includes(`${moduleKey}:write`) || permissions.includes(moduleKey);

  const toggleReadWrite = (moduleKey, action) => {
    const readPerm = `${moduleKey}:read`;
    const writePerm = `${moduleKey}:write`;
    const isRead = action === "read";

    setPermissions((prev) => {
      let next = prev.filter((p) => p !== moduleKey && p !== readPerm && p !== writePerm);
      if (isRead) {
        if (!hasRead(moduleKey)) next = next.concat(readPerm);
      } else {
        if (!hasWrite(moduleKey)) next = next.concat(writePerm);
      }
      return next;
    });
  };

  // Legacy "inventory" (full) or "inventory:write" grants (from before the
  // Issue/Purchase/Edit/History split) are expanded into explicit granular
  // permissions the first time any inventory checkbox is touched, so the
  // checkboxes stay editable/deterministic instead of being stuck "on".
  const expandLegacyInventory = (perms) => {
    if (perms.includes("inventory") || perms.includes("inventory:write")) {
      const rest = perms.filter((p) => p !== "inventory" && p !== "inventory:write" && !p.startsWith("inventory:"));
      return rest.concat(
        "inventory:read",
        INVENTORY_ACTIONS.map((a) => `inventory:${a.value}`)
      );
    }
    return perms;
  };

  const hasInventoryRead = () =>
    permissions.includes("inventory:read") ||
    permissions.includes("inventory") ||
    permissions.includes("inventory:write") ||
    INVENTORY_ACTIONS.some((a) => permissions.includes(`inventory:${a.value}`));

  const hasInventoryAction = (action) =>
    permissions.includes(`inventory:${action}`) ||
    permissions.includes("inventory") ||
    permissions.includes("inventory:write");

  const toggleInventoryRead = () => {
    setPermissions((prev) => {
      const next = expandLegacyInventory(prev);
      return next.includes("inventory:read")
        ? next.filter((p) => p !== "inventory:read")
        : next.concat("inventory:read");
    });
  };

  const toggleInventoryAction = (action) => {
    const perm = `inventory:${action}`;
    setPermissions((prev) => {
      const next = expandLegacyInventory(prev);
      return next.includes(perm) ? next.filter((p) => p !== perm) : next.concat(perm);
    });
  };

  const toggleLegacy = (value) => {
    if (permissions.includes(value)) {
      setPermissions(permissions.filter((p) => p !== value));
    } else {
      setPermissions([...permissions, value]);
    }
  };

  const hasFullSettings = permissions.includes("settings");
  const toggleSettingsFull = () => {
    if (hasFullSettings) {
      setPermissions(permissions.filter((p) => p !== "settings" && !p.startsWith("settings-")));
    } else {
      setPermissions([
        ...permissions.filter((p) => !p.startsWith("settings-")),
        "settings",
      ]);
    }
  };

  const toggleSettingsSub = (value) => {
    if (hasFullSettings) return;
    if (permissions.includes(value)) {
      setPermissions(permissions.filter((p) => p !== value));
    } else {
      setPermissions(permissions.filter((p) => p !== "settings").concat(value));
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError("Role name is required");
      return;
    }
    const permsToSend = isSchoolAdmin ? permissions.filter((p) => p !== "settings-admin") : permissions;
    try {
      setError(null);
      setLoading(true);
      const token = localStorage.getItem("token");
      if (isEdit) {
        await axiosInstance.put(API_ENDPOINTS.schoolRole(role._id), { name: name.trim(), permissions: permsToSend }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axiosInstance.post(API_ENDPOINTS.schoolRoles, { name: name.trim(), permissions: permsToSend }, { headers: { Authorization: `Bearer ${token}` } });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || (isEdit ? "Failed to update role" : "Failed to create role"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-6">
        <h2 className="text-xl font-semibold mb-4">{isEdit ? "Edit role" : "Create role"}</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Define a role name and which permissions it has. Later you can assign this role to staff in Staff permissions.
        </p>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-4">
          <div>
            <Label>Role name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Accountant, HR" className="mt-1" />
          </div>
          <div>
            <Label className="mb-2 block">Permissions</Label>
            <div className="border rounded-lg p-3 bg-muted/30 max-h-[420px] overflow-y-auto space-y-4">
              {visibleReadWriteModules.map((m) => (
                <div key={m.key} className="flex flex-wrap items-center gap-4">
                  <span className="text-sm font-medium min-w-[100px]">{m.label}</span>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${m.key}-read`}
                        checked={hasRead(m.key)}
                        onCheckedChange={() => toggleReadWrite(m.key, "read")}
                      />
                      <label htmlFor={`role-${m.key}-read`} className="text-sm">Read</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`role-${m.key}-write`}
                        checked={hasWrite(m.key)}
                        onCheckedChange={() => toggleReadWrite(m.key, "write")}
                      />
                      <label htmlFor={`role-${m.key}-write`} className="text-sm">Write</label>
                    </div>
                  </div>
                </div>
              ))}
              {showInventorySection && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="role-inventory-read"
                      checked={hasInventoryRead()}
                      onCheckedChange={toggleInventoryRead}
                    />
                    <label htmlFor="role-inventory-read" className="text-sm font-medium">Inventory</label>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 pl-6">
                    {INVENTORY_ACTIONS.filter((a) => a.value !== "analytics").map((a) => (
                      <div key={a.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={`role-inventory-${a.value}`}
                          checked={hasInventoryAction(a.value)}
                          onCheckedChange={() => toggleInventoryAction(a.value)}
                        />
                        <label htmlFor={`role-inventory-${a.value}`} className="text-sm">{a.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {visibleLegacy.map((p) => (
                <div key={p.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`role-${p.value}`}
                    checked={permissions.includes(p.value)}
                    onCheckedChange={() => toggleLegacy(p.value)}
                  />
                  <label htmlFor={`role-${p.value}`} className="text-sm">{p.label}</label>
                </div>
              ))}
              {showInventorySection && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="role-inventory-analytics"
                    checked={hasInventoryAction("analytics")}
                    onCheckedChange={() => toggleInventoryAction("analytics")}
                  />
                  <label htmlFor="role-inventory-analytics" className="text-sm">Analytics</label>
                </div>
              )}
              {showSettingsSection && (
                <div className="pt-2 border-t space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="role-settings-full"
                      checked={hasFullSettings}
                      onCheckedChange={toggleSettingsFull}
                    />
                    <label htmlFor="role-settings-full" className="text-sm font-medium">Settings (full access)</label>
                  </div>
                  {visibleSettingsSub.length > 0 && (
                    <>
                      <p className="text-xs text-muted-foreground pl-6">Or only these:</p>
                      <div className="grid grid-cols-2 gap-2 pl-6">
                        {visibleSettingsSub.map((p) => (
                          <div key={p.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`role-${p.value}`}
                              checked={hasFullSettings || permissions.includes(p.value)}
                              disabled={hasFullSettings}
                              onCheckedChange={() => toggleSettingsSub(p.value)}
                            />
                            <label htmlFor={`role-${p.value}`} className="text-sm">{p.label}</label>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !name.trim()}>{loading ? "Saving..." : isEdit ? "Update role" : "Create role"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [permissionsList, setPermissionsList] = useState([]);
  const [schoolAllowedPermissions, setSchoolAllowedPermissions] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance.get(API_ENDPOINTS.permissionsList).then((res) => {
      setPermissionsList(res.data?.data?.permissions ?? []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let user = null;
    try {
      user = JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      user = null;
    }
    const schoolId = user?.school && (typeof user.school === "object" ? user.school._id : user.school);
    if (!schoolId) {
      return;
    }
    axiosInstance
      .get(API_ENDPOINTS.schoolAllowedPermissions(schoolId))
      .then((res) => {
        setSchoolAllowedPermissions(res.data?.data?.permissions ?? []);
      })
      .catch(() => {
        setSchoolAllowedPermissions([]);
      });
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      setError(null);
      const res = await axiosInstance.get(API_ENDPOINTS.schoolRoles);
      setRoles(res.data?.data?.roles ?? []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load roles");
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const openAdd = () => {
    setSelectedRole(null);
    setShowModal(true);
  };
  const openEdit = (role) => {
    setSelectedRole(role);
    setShowModal(true);
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? Staff with this role will need to be reassigned.`)) return;
    try {
      await axiosInstance.delete(API_ENDPOINTS.schoolRole(role._id));
      fetchRoles();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete role");
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="flex justify-between items-center mb-6">
          <p className="text-sm text-muted-foreground">Create role types and assign permissions. Then assign roles to staff in the next tab.</p>
          <Button onClick={openAdd}><Plus className="h-4 w-4 mr-2" />Add role</Button>
        </div>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading roles...</div>
        ) : roles.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground border rounded-lg">No roles yet. Create a role to get started.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role name</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role._id}>
                  <TableCell className="font-medium">{role.name}</TableCell>
                  <TableCell>{role.permissions?.length ?? 0} permission(s)</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" className="mr-2" onClick={() => openEdit(role)}><Edit className="h-4 w-4 mr-1" />Edit</Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(role)}><Trash className="h-4 w-4 mr-1" />Delete</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {showModal && (
          <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <RoleForm role={selectedRole} onClose={() => setShowModal(false)} onSuccess={fetchRoles} permissionsList={permissionsList} allowedPermissionsFromSchool={schoolAllowedPermissions} />
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}
